import React, { Fragment, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormProvider, useForm } from "react-hook-form";
import { Edit } from "@mui/icons-material";
import NewUserForm from "../../../../components/modals/bookingModalComponents/components/NewUserForm";
import { splitName } from "../utils/importUsersHelpers";
import { useTranslation } from "react-i18next";

function buildDefaultValues(user) {
  const { firstName, lastName } = splitName(user?.name);
  return {
    user: {
      userDetails: {
        firstName: user?.firstName ?? firstName,
        lastName: user?.lastName ?? lastName,
        email: user?.email ?? "",
        phoneNumber: user?.phoneNumber ?? "",
        sendEmailNotifications: user?.sendEmailNotifications ?? true,
      },
    },
  };
}

function didUserFormChange(data, defaultValues) {
  const submitted = data.user.userDetails;
  const original = defaultValues.user.userDetails;

  return (
    submitted.firstName !== original.firstName ||
    submitted.lastName !== original.lastName ||
    (submitted.email ?? "") !== (original.email ?? "") ||
    (submitted.phoneNumber ?? "") !== (original.phoneNumber ?? "")
  );
}

export default function EditUserDialog({ user, onSave }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  function toggle() {
    setOpen((prev) => !prev);
  }

  const defaultValues = useMemo(() => buildDefaultValues(user), [user]);
  const methods = useForm({ defaultValues });

  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
    }
  }, [open, user]);

  function handleSave(data) {
    const wasChanged = didUserFormChange(data, defaultValues);
    const { firstName, lastName, email, phoneNumber } = data.user.userDetails;

    const updatedUser = {
      ...user,
      firstName,
      lastName,
      name: lastName ? `${firstName} ${lastName}`.trim() : firstName,
      email,
      phoneNumber,
      updated: user?.updated || wasChanged,
    };

    onSave(updatedUser);
    toggle();
  }

  return (
    <Fragment>
      <Box>
        <IconButton size="small" title={t("EditUser")} onClick={toggle}>
          <Edit fontSize="small" />
        </IconButton>
      </Box>
      <Dialog open={open} onClose={toggle} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {user?.name}
          <IconButton onClick={toggle} size="small" color="error">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <FormProvider {...methods}>
          <DialogContent>
            <Grid container spacing={2} mt={2}>
              <NewUserForm showSendEmail={false} requireNames={false} />
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={toggle} color="error">
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={methods.handleSubmit(handleSave)}
            >
              {t("Save")}
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </Fragment>
  );
}
